/**
 * Garminアクティビティカード
 *
 * タイムラインに表示するGarminワークアウトカード
 */

import { Heart, Timer, TrendingUp } from 'lucide-react'

import type { GarminActivitySummary } from '@/api/types/garmin'
import { formatDistanceMeters, formatTimeJST } from '@/lib/activity-format-utils'
import { getGarminActivityStyle } from '@/lib/garmin-activity-config'
import { formatSecondsJa } from '@/lib/time-format'
import { cn } from '@/lib/utils'

type Props = {
  activity: GarminActivitySummary
  className?: string
}

export const GarminActivityCard = ({ activity, className }: Props) => {
  const style = getGarminActivityStyle(activity.activity_type)
  const Icon = style.icon

  return (
    <div className={cn('rounded-lg border bg-card p-3 shadow-sm', className)}>
      <div className="flex items-start gap-3">
        {/* アイコン */}
        <div className={cn('rounded-lg p-2', style.bgColor)}>
          <Icon className={cn('size-5', style.color)} />
        </div>

        {/* 内容 */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <h3 className="font-medium text-sm truncate">{activity.activity_name}</h3>
            <span className="text-xs text-muted-foreground shrink-0">{formatTimeJST(activity.start_time)}</span>
          </div>

          <p className="text-xs text-muted-foreground mt-0.5">{activity.activity_type}</p>

          {/* メトリクス */}
          <div className="flex items-center gap-3 mt-2 text-xs">
            <div className="flex items-center gap-1">
              <Timer className="size-3 text-muted-foreground" />
              <span className="tabular-nums">{formatSecondsJa(activity.duration_seconds)}</span>
            </div>

            {activity.distance_meters && activity.distance_meters > 0 && (
              <div className="flex items-center gap-1">
                <TrendingUp className="size-3 text-muted-foreground" />
                <span className="tabular-nums">{formatDistanceMeters(activity.distance_meters)}</span>
              </div>
            )}

            {activity.avg_hr && (
              <div className="flex items-center gap-1">
                <Heart className="size-3 text-red-400" />
                <span className="tabular-nums">{activity.avg_hr} bpm</span>
              </div>
            )}

            {activity.calories && <span className="text-orange-500 tabular-nums">{activity.calories} kcal</span>}
          </div>
        </div>
      </div>
    </div>
  )
}
